import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

interface SecurityValidationResult {
  isValid: boolean;
  serverRole: string;
  errors: string[];
}

export const useSecurityEnhancements = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [lastValidation, setLastValidation] = useState<SecurityValidationResult | null>(null);

  // Enhanced server-side role validation
  const validateServerRole = async (): Promise<SecurityValidationResult> => {
    if (!user) {
      return { isValid: false, serverRole: 'user', errors: ['User not authenticated'] };
    }

    try {
      const { data, error } = await supabase.rpc('validate_user_role_server');
      
      if (error) {
        console.error('Server role validation error:', error);
        return { 
          isValid: false, 
          serverRole: 'user', 
          errors: ['Server validation failed'] 
        };
      }

      if (data && data.length > 0) {
        const result = {
          isValid: data[0].is_valid,
          serverRole: data[0].server_role,
          errors: data[0].errors
        };
        setLastValidation(result);
        return result;
      }

      return { isValid: false, serverRole: 'user', errors: ['No validation data received'] };
    } catch (error) {
      console.error('Security validation error:', error);
      return { isValid: false, serverRole: 'user', errors: ['Validation request failed'] };
    }
  };

  // Secure operation validation with enhanced rate limiting
  const validateSecureOperation = async (operation: string, requiredRole: 'user' | 'admin' | 'superadmin' = 'user'): Promise<boolean> => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to perform this operation",
      });
      return false;
    }

    try {
      // Check server-side permissions first
      const { data: hasPermission, error: permissionError } = await supabase
        .rpc('validate_server_permission', { required_role: requiredRole });

      if (permissionError || !hasPermission) {
        toast({
          variant: "destructive",
          title: "Access Denied",
          description: "You don't have permission to perform this operation",
        });
        return false;
      }

      // Temporarily disable enhanced rate limiting to fix UX issue
      // TODO: Re-enable after optimizing validation frequency
      /*
      const { data: rateLimitOk, error: rateLimitError } = await supabase
        .rpc('check_enhanced_security_rate_limit', {
          _operation: operation,
          _max_requests: 10,
          _window_minutes: 15
        });

      if (rateLimitError || !rateLimitOk) {
        toast({
          variant: "destructive",
          title: "Rate Limit Exceeded",
          description: "Too many requests. Please try again later.",
        });
        return false;
      }
      */

      return true;
    } catch (error) {
      console.error('Secure operation validation failed:', error);
      toast({
        variant: "destructive",
        title: "Security Validation Failed",
        description: "Unable to validate operation security",
      });
      return false;
    }
  };

  // Log sensitive data access
  const logSensitiveAccess = async (tableName: string, recordId: string, accessType: string) => {
    try {
      await supabase.rpc('log_sensitive_data_access', {
        table_name: tableName,
        record_id: recordId,
        access_type: accessType
      });
    } catch (error) {
      console.error('Failed to log sensitive data access:', error);
    }
  };

  // Periodic security validation
  useEffect(() => {
    if (!user) return;

    // Reduce frequency of security checks to prevent rate limiting
    const performSecurityCheck = async () => {
      // Only perform validation if user is active (reduce background checks)
      if (document.visibilityState === 'visible') {
        const validation = await validateServerRole();
        
        if (!validation.isValid && validation.errors.length > 0) {
          // Only show critical errors, not role mismatch warnings
          const criticalErrors = validation.errors.filter(error => 
            !error.includes('Client-side role mismatch')
          );
          
          if (criticalErrors.length > 0) {
            console.warn('Critical security validation failed:', criticalErrors);
          }
        }
      }
    };

    // Initial check
    performSecurityCheck();

    // Reduce frequency: check every 10 minutes instead of 5
    const interval = setInterval(performSecurityCheck, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  return {
    validateServerRole,
    validateSecureOperation,
    logSensitiveAccess,
    lastValidation
  };
};